import {AbstractParameter} from 'parameters/AbstractParameter.js';
import SubMacro from './SubMacro.js';
import {setEffectParameterValues} from 'actions/index.js';
import ParameterContext from 'effects/ParameterContext.js';
import {
    saveParameterToJson,
    loadParameterFromJson} from 'parameters/parameterJsonFunctions.js';
import {AbstractAudioParamParameter} from 'parameters/AbstractAudioParamParameter.js';
import {A_RATE, K_RATE} from 'constants/automation-rates.js';
import Tone from 'tone';
import {uuid} from 'uuidv4';

class RackMacro {
    constructor(name, sequencerEngine, context) {
        this.id = uuid();
        this.context = context.getCopy().forMacro(this);
        this.sequencerEngine = sequencerEngine;
        this.name = name;

        // a macro is a collection of sub macros
        this.subMacros = [];

        // macros containg positioning data
        this.x = 100;
        this.y = 110;

        this.signal = new Tone.Signal(0);
        this.macroParameter = new AbstractAudioParamParameter(
            'macro',
            this.signal,
            (signal, value) => {
                let update = {};
                for (let i in this.subMacros) {
                    let subMacro = this.subMacros[i];
                    let ret = subMacro.updateRedux(value);
                    update[subMacro.parameter.id] = ret.value;
                }
                this.sequencerEngine.store.dispatch(
                    setEffectParameterValues(update));
            },
            {
                min: 0,
                max: 1,
                defaultValue: 0,
                automationRate: A_RATE
            });

        this.macroParameter.enableAudioParam();
        
        this.parameters = [];
        this.registerParameter(this.macroParameter);
    }

    registerParameter(parameter) {
        if (parameter !== undefined) {
            parameter.name = parameter.name;
            parameter.setStore(this.sequencerEngine.store);
            parameter.setContext(this.context);
            this.parameters.push(parameter);
        }
    }

    addParameter(parameter, min=parameter.min, max=parameter.max) {
        // by default use min and max defined in effect
        let subMacro = new SubMacro(
            parameter, min, max, this);

        if (parameter.audioParam || parameter.audioParams) {
            this.signal.connect(subMacro.scaler);
        }
        this.subMacros.push(subMacro);
        this.macroParameter.set(
            this.macroParameter.get(true));
        parameter.hasMacro = this;
    }

    unmapAllParameters() {
        let parameters = this.subMacros.map(s => s.parameter);
        parameters.forEach(
            p => this.unmapParameter(p));
    }

    ripMods() {
        this.macroParameter.disconnectFromAllModulators();
    }

    isParameterMapped(parameter) {
        return this.subMacros.some(
            subMacro => subMacro.parameter === parameter);
    }

    normalizeParameterValue(parameter, value) {
        let subMacro = this.subMacros.find(
            subMacro => subMacro.parameter === parameter);
        return subMacro.normalizeValue(value);
    }

    unmapParameter(parameter, dispose=true) {
        let subMacro = this.subMacros.find(
            subMacro => subMacro.parameter === parameter);

        if (subMacro === undefined) {
            // not mapped
            return;
        }

        parameter.hasMacro = undefined;

        if (subMacro.parameter.audioParam || subMacro.parameter.audioParams) {
            this.signal.disconnect(subMacro.scaler);
        }
        let index = this.subMacros.indexOf(subMacro);
        if (dispose) {
            subMacro.dispose();
        }
        this.subMacros.splice(index, 1);

        parameter.set(parameter.get(), true, true);
    }

    dispose() {
        this.unmapAllParameters();
        this.subMacros = undefined;
        this.macroParameter.dispose();
        this.signal = undefined;
        this.macroParameter = undefined;
    }

    getJson() {
        let json = {
            id: this.id,
            x: this.x,
            y: this.y,
            name: this.name, 
            subMacros: this.subMacros.map(
                subMacro => subMacro.getJson()),
            macroValue: this.macroParameter.get(true)
        };

        json = saveParameterToJson(
            json,
            'macroParameter',
            this.macroParameter,
            true);

        return json;
    }

    getParameters() {
        return this.parameters;
    }

    fromJson(json, parameters) {
        if (json.id) {
            this.id = json.id;
        }
        this.name = json.name;
        this.x = json.x;
        this.y = json.y;

        for (let i in json.subMacros) {
            let {parameter, min, max} = json.subMacros[i];
            let _parameter = parameter;
            parameter = parameters.find(p => p.id === parameter);
            if (parameter === undefined) {
                continue;
            }
            this.addParameter(parameter, min, max);
        }

        this.macroParameter.set(json.macroValue);

        loadParameterFromJson(
            json,
            'macroParameter',
            this.macroParameter,
            this.sequencerEngine);
    }
}

export default RackMacro;
